/**
 * Czytelny komunikat odrzuconego zapisu kontrahenta — JEDNO źródło dla formularza
 * (`ContractorDetail`) i tablicy Kanban (`useContractorKanbanMove`). Oba miejsca wysyłają ten sam
 * zapis (`PATCH /api/contractors/:id`), więc odrzucenie ma brzmieć tak samo, niezależnie od tego,
 * czy zmiana przyszła z formularza, czy z przeciągnięcia karty.
 *
 * Pokrywa błędy, które kończą się samym komunikatem: walidacja pól, słowniki, opiekun/handlowiec,
 * zajęty skrót, brak prawa edycji. NIE pokrywa gałęzi z własnym zachowaniem — kontrola kartoteki
 * (`useControlWarning`: dialog/treść procedury), blokada zadania przycisku SQL (komunikat z opisem
 * zadania) i zajęty NIP (okno istniejącego kontrahenta) — te obsługuje wołający przed tym helperem.
 *
 * `null` = błąd spoza listy; wołający zostaje przy swoim ogólnym komunikacie.
 */

import type { TFunction } from 'i18next';

import { extractErrorCode } from '@/lib/errors';
import { mapDictionarySaveError } from '@/lib/records/save-error-message';
import { buildValidationToastMessage } from '@/lib/validation-toast';

export function contractorSaveErrorMessage(
  err: unknown,
  t: TFunction,
  fieldLabel: (field: string) => string | null,
): string | null {
  const code = extractErrorCode(err);
  if (code === 'VALIDATION_ERROR') {
    return (
      buildValidationToastMessage(err, fieldLabel, t) ??
      t('tenant.contractors.toast.validationError')
    );
  }
  // Odrzucona część słownikowa zapisu (pozycja spoza słownika / brak lub zła wartość
  // parametru słownika) — czytelna przyczyna zamiast ogólnego „błąd zapisu".
  const dictionaryMsg = mapDictionarySaveError(err, t);
  if (dictionaryMsg !== null) return dictionaryMsg;
  // Walidacja opiekuna/handlowca (PATCH 400) — komunikat mówi, KTÓRE pole odrzucono i dlaczego.
  if (code === 'OWNER_USER_NOT_FOUND' || code === 'OWNER_NO_COMPANY_ACCESS') {
    return t('tenant.contractors.errors.ownerRejected');
  }
  if (code === 'ASSIGNED_USER_NOT_FOUND' || code === 'ASSIGNED_NO_COMPANY_ACCESS') {
    return t('tenant.contractors.errors.assignedRejected');
  }
  if (code === 'SHORT_NAME_TAKEN') return t('tenant.contractors.errors.shortNameTaken');
  // Prawo edycji kontrahenta liczy serwer per rekord (firma, kategoria, zakres „moje") — panel zna
  // tylko prawo na poziomie kartoteki, więc odmowa dla konkretnego rekordu jest możliwa.
  if (code === 'PERMISSION_DENIED') return t('tenant.contractors.errors.editDenied');
  return null;
}
